import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Alert, Button, useMediaQuery, useTheme } from "@mui/material";
import { useDashboardState } from "../hooks/useDashboardState";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { useDashboardQuickActions } from "../hooks/useDashboardQuickActions";

import DashboardSkeleton from "../components/Dashboard/DashboardSkeleton";
import { navigateToDriveUpload } from "../utils/driveNavigation";
import PendingApproval from "../components/Dashboard/PendingApproval";
import DashboardMobileHome from "../components/Dashboard/DashboardMobileHome";
import DashboardDesktopHome from "../components/Dashboard/DashboardDesktopHome";
import { User } from "../services/api";

import { USER_MANAGEMENT, VIEW_NOTICES, PROFILE } from "../routes";
import PageContainer from "../components/PageContainer";
import { useAccountHandlers } from "../hooks/useAccountHandlers";
import {
  type DashboardModalsProps,
} from "../components/Dashboard/DashboardModals";
import { useRegisterPageActions } from "../context/PageActionsContext";
import { DASHBOARD_TUTORIAL_VIDEOS } from "../constants/dashboardTutorialVideos";
import { usePendingJoinRequestCount } from "../hooks/usePendingJoinRequestCount";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobileDashboard = useMediaQuery(theme.breakpoints.down("md"));
  const { user, token, updateUser, logout } = useAuth();
  const {
    isAdCenterEnabled,
    isUserManagementEnabled,
    isCircleManagementEnabled,
    isTeamManagementEnabled,
  } = useFeatureFlags();

  const {
    data,
    notices,
    activeAdverts,
    draftNotices,
    upcomingEvents,
    display,
    hasSubcircles,
    needsOnboarding,
    filteredNoticesByDay,
    chartTitle,
    isLoading,
    error,
    refetch,
  } = useDashboardState();

  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const [infoNoticeModalOpen, setInfoNoticeModalOpen] = useState(false);
  const [sendImageModalOpen, setSendImageModalOpen] = useState(false);
  const [sendAttachmentModalOpen, setSendAttachmentModalOpen] = useState(false);
  const [addEventModalOpen, setAddEventModalOpen] = useState(false);
  const [selectedEventDate, setSelectedEventDate] = useState<Date | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);
  const [isDirectNoticeLauncherOpen, setIsDirectNoticeLauncherOpen] =
    useState(false);
  const [directNoticeSelection, setDirectNoticeSelection] = useState<{
    user: User;
    mode: "direct_notice" | "dm_event";
  } | null>(null);

  const {
    handleLogoFileUpload,
    handleUpdateSchoolDescription,
    handleBannerFileUpload,
    handleUpdatePhoneNumber,
  } = useAccountHandlers({ user, updateUser, logout, navigate, token });

  const handleCreateNotice = useCallback(
    () => navigate("/create-notice"),
    [navigate],
  );
  const handleOpenInfoNoticeModal = useCallback(() => {
    setInfoNoticeModalOpen(true);
  }, []);
  const handleOpenSendImageModal = useCallback(() => {
    setSendImageModalOpen(true);
  }, []);
  const handleOpenSendAttachmentModal = useCallback(() => {
    setSendAttachmentModalOpen(true);
  }, []);
  const handleViewNotices = useCallback(
    () => navigate(VIEW_NOTICES),
    [navigate],
  );
  const handleAdCenter = useCallback(() => navigate("/ad-center"), [navigate]);
  const handleUserManagement = useCallback(
    () => navigate(USER_MANAGEMENT),
    [navigate],
  );
  const handleViewJoinRequests = useCallback(
    () => navigate(`${USER_MANAGEMENT}?tab=requests`),
    [navigate],
  );

  const joinRequestsCount = usePendingJoinRequestCount();
  const handleCirclesManagement = useCallback(
    () => navigate("/circles"),
    [navigate],
  );
  const handleCloseDirectNoticeLauncher = useCallback(() => {
    setIsDirectNoticeLauncherOpen(false);
  }, []);
  const handleOpenDirectNoticeLauncher = useCallback(() => {
    setIsDirectNoticeLauncherOpen(true);
  }, []);
  const handleSelectDirectNoticeUser = useCallback(
    (selectedUser: User, mode: "direct_notice" | "dm_event") => {
      setDirectNoticeSelection({ user: selectedUser, mode });
      setIsDirectNoticeLauncherOpen(false);
    },
    []
  );
  const handleCloseDirectNotice = useCallback(() => {
    setDirectNoticeSelection(null);
  }, []);

  const handleCalendarDateClick = useCallback(
    (slotInfo: { start: Date; end: Date }) => {
      setSelectedEventDate(slotInfo.start);
      setAddEventModalOpen(true);
    },
    [],
  );

  const handleOnboardingComplete = useCallback(() => {
    setIsOnboardingModalOpen(false);
  }, []);

  const handleCloseInfoNotice = useCallback(() => {
    setInfoNoticeModalOpen(false);
  }, []);

  const handleCloseSendImage = useCallback(() => {
    setSendImageModalOpen(false);
  }, []);

  const handleCloseSendAttachment = useCallback(() => {
    setSendAttachmentModalOpen(false);
  }, []);

  const handleCloseAddEvent = useCallback(() => {
    setAddEventModalOpen(false);
    setSelectedEventDate(null);
  }, []);

  const handleOpenDriveUpload = useCallback(() => {
    navigateToDriveUpload(navigate);
  }, [navigate]);

  const handleCloseVideo = useCallback(() => {
    setSelectedVideo(null);
  }, []);

  const handlePlayVideo = useCallback((videoPath: string, title: string) => {
    setSelectedVideo({ videoPath, title });
  }, []);

  const handleOpenAddEventModal = useCallback(() => {
    setAddEventModalOpen(true);
  }, []);

  const navigateToCalendar = useCallback(
    () => navigate("/calendar"),
    [navigate],
  );

  const navigateToTeamManagement = useCallback(
    () => navigate("/team-management"),
    [navigate],
  );

  const {
    heroQuickActions,
    mobileHomeQuickActions,
    mobileDrawerQuickActions,
    desktopChartQuickActions,
  } = useDashboardQuickActions({
    theme,
    handlers: {
      handleCreateNotice,
      handleOpenInfoNoticeModal,
      handleOpenSendImageModal,
      handleOpenSendAttachmentModal,
      handleViewNotices,
      handleCirclesManagement,
      handleUserManagement,
      handleOpenDriveUpload,
      handleOpenDirectNoticeLauncher,
      handleOpenAddEventModal,
      navigateToCalendar,
      navigateToTeamManagement,
    },
    flags: {
      isCircleManagementEnabled,
      isUserManagementEnabled,
      isTeamManagementEnabled,
      isMobileDashboard,
    },
  });

  const pageActions = useMemo(
    () => ({
      onMainAction: handleCreateNotice,
      onVideoPlay: () =>
        handlePlayVideo(
          DASHBOARD_TUTORIAL_VIDEOS.createNotice.path,
          DASHBOARD_TUTORIAL_VIDEOS.createNotice.title,
        ),
      onOpenInfoNotice: handleOpenInfoNoticeModal,
      onOpenSendImage: handleOpenSendImageModal,
      onOpenAddEvent: handleOpenAddEventModal,
      onOpenSendAttachment: handleOpenSendAttachmentModal,
      onOpenUploadDrive: handleOpenDriveUpload,
      count: data?.totalNotices || 0,
    }),
    [
      handleCreateNotice,
      handlePlayVideo,
      handleOpenInfoNoticeModal,
      handleOpenSendImageModal,
      handleOpenSendAttachmentModal,
      handleOpenDriveUpload,
      handleOpenAddEventModal,
      data?.totalNotices,
    ],
  );
  useRegisterPageActions(pageActions);

  const mobileGreetingName = useMemo(() => {
    if (user?.userType === "team" && user?.fullName?.trim()) {
      return user.fullName.trim().split(/\s+/)[0];
    }
    if (user?.schoolName?.trim()) {
      return user.schoolName.trim();
    }
    return "Noticeboard";
  }, [user?.fullName, user?.schoolName, user?.userType]);

  useEffect(() => {
    if (needsOnboarding && !sessionStorage.getItem("onboardingShown")) {
      sessionStorage.setItem("onboardingShown", "true");
      setIsOnboardingModalOpen(true);
    }
  }, [needsOnboarding]);

  const dashboardModalsProps = useMemo((): DashboardModalsProps => {
    return {
      user,
      token,
      isOnboardingModalOpen,
      onOnboardingComplete: handleOnboardingComplete,
      handleLogoFileUpload,
      handleUpdateSchoolDescription,
      handleBannerFileUpload,
      handleUpdatePhoneNumber,
      infoNoticeModalOpen,
      onCloseInfoNotice: handleCloseInfoNotice,
      sendImageModalOpen,
      onCloseSendImage: handleCloseSendImage,
      sendAttachmentModalOpen,
      onCloseSendAttachment: handleCloseSendAttachment,
      addEventModalOpen,
      selectedEventDate,
      onCloseAddEvent: handleCloseAddEvent,
      onNoticeDataRefresh: refetch,
      selectedVideo,
      onCloseVideo: handleCloseVideo,
      isDirectNoticeLauncherOpen,
      onCloseDirectNoticeLauncher: handleCloseDirectNoticeLauncher,
      onSelectDirectNoticeUser: handleSelectDirectNoticeUser,
      directNoticeSelection,
      onCloseDirectNotice: handleCloseDirectNotice,
    };
  }, [
    user,
    token,
    isOnboardingModalOpen,
    handleOnboardingComplete,
    handleLogoFileUpload,
    handleUpdateSchoolDescription,
    handleBannerFileUpload,
    handleUpdatePhoneNumber,
    infoNoticeModalOpen,
    handleCloseInfoNotice,
    sendImageModalOpen,
    handleCloseSendImage,
    sendAttachmentModalOpen,
    handleCloseSendAttachment,
    addEventModalOpen,
    selectedEventDate,
    handleCloseAddEvent,
    refetch,
    selectedVideo,
    handleCloseVideo,
    isDirectNoticeLauncherOpen,
    handleCloseDirectNoticeLauncher,
    handleSelectDirectNoticeUser,
    directNoticeSelection,
    handleCloseDirectNotice,
  ]);

  const checklistItems = useMemo(
    () => [
      ...(isUserManagementEnabled
        ? [
            {
              label: "Add your first user",
              done: (data?.totalUsers || 0) > 0,
              onClick: handleUserManagement,
              videoPath: DASHBOARD_TUTORIAL_VIDEOS.addUser.path,
              videoTitle: DASHBOARD_TUTORIAL_VIDEOS.addUser.title,
            },
          ]
        : []),
      {
        label: "Create your first notice",
        done: (data?.totalNotices || 0) > 0,
        onClick: handleCreateNotice,
        videoPath: DASHBOARD_TUTORIAL_VIDEOS.createNotice.path,
        videoTitle: DASHBOARD_TUTORIAL_VIDEOS.createNotice.title,
      },
      ...(isCircleManagementEnabled
        ? [
            {
              label: "Add your subcircle",
              done: hasSubcircles,
              onClick: handleCirclesManagement,
              videoPath: DASHBOARD_TUTORIAL_VIDEOS.createCircle.path,
              videoTitle: DASHBOARD_TUTORIAL_VIDEOS.createCircle.title,
            },
          ]
        : []),
    ],
    [
      data,
      hasSubcircles,
      handleUserManagement,
      handleCreateNotice,
      handleCirclesManagement,
      isUserManagementEnabled,
      isCircleManagementEnabled,
    ],
  );

  const stats = useMemo(
    () => ({
      totalUsers: data?.totalUsers || 0,
      totalNotices: data?.totalNotices || 0,
      upcomingEvents,
      activeAdverts,
    }),
    [activeAdverts, data?.totalNotices, data?.totalUsers, upcomingEvents],
  );

  const onCompleteSetup = useCallback(() => navigate(PROFILE), [navigate]);

  // Before the loading/error checks: unapproved schools only ever see this
  // screen, so they shouldn't sit on the skeleton or see a stats-fetch error.
  if (user?.userType !== "team" && !user?.isSchoolApproved) {
    return (
      <PageContainer>
        <PendingApproval onViewProfile={() => navigate(PROFILE)} />
      </PageContainer>
    );
  }

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <PageContainer>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          Failed to load dashboard data.
        </Alert>
      </PageContainer>
    );
  }

  if (isMobileDashboard) {
    return (
      <DashboardMobileHome
        mobileGreetingName={mobileGreetingName}
        onCompleteSetup={onCompleteSetup}
        handleCreateNotice={handleCreateNotice}
        handleOpenAddEventModal={handleOpenAddEventModal}
        handlePlayVideo={handlePlayVideo}
        handleViewNotices={handleViewNotices}
        handleAdCenter={handleAdCenter}
        mobileHomeQuickActions={mobileHomeQuickActions}
        mobileDrawerQuickActions={mobileDrawerQuickActions}
        draftNoticesCount={draftNotices.length}
        joinRequestsCount={joinRequestsCount}
        handleViewJoinRequests={handleViewJoinRequests}
        display={display}
        isAdCenterEnabled={isAdCenterEnabled}
        checklistItems={checklistItems}
        stats={stats}
        dashboardModalsProps={dashboardModalsProps}
      />
    );
  }

  return (
    <DashboardDesktopHome
      schoolName={user?.schoolName}
      userType={user?.userType}
      fullName={user?.fullName}
      orgId={user?.orgId}
      notices={notices}
      onCompleteSetup={onCompleteSetup}
      heroQuickActions={heroQuickActions}
      draftNoticesCount={draftNotices.length}
      joinRequestsCount={joinRequestsCount}
      handleViewNotices={handleViewNotices}
      handleViewJoinRequests={handleViewJoinRequests}
      display={display}
      isAdCenterEnabled={isAdCenterEnabled}
      handleAdCenter={handleAdCenter}
      checklistItems={checklistItems}
      handlePlayVideo={handlePlayVideo}
      desktopChartQuickActions={desktopChartQuickActions}
      filteredNoticesByDay={filteredNoticesByDay}
      chartTitle={chartTitle}
      noticesByType={data?.noticesByType || []}
      handleCalendarDateClick={handleCalendarDateClick}
      handleCreateNotice={handleCreateNotice}
      stats={stats}
      dashboardModalsProps={dashboardModalsProps}
    />
  );
};

export default Dashboard;
